<template>
    <div class="relative border border-gray-300 bg-gray-warm-50 rounded-md px-6 py-5">
        <h2 class="text-gray-400 text-lg font-semibold pb-2">
            Documents
        </h2>

        <p v-if="editable && ! envelope_documents.length"
            class="text-base text-gray-500 mb-3"
        >
            Select one or more documents to include in this Envelope.
        </p>

        <div v-if="editable"
            class="flex flex-col pb-3"
        >
            <p v-if="envelope_documents?.length > 0"
                class="text-sm text-gray-500 mb-4 leading-snug"
            >
                {{ envelope_documents.length === 1 ? '1 document in this envelope' : `${envelope_documents.length} documents in this envelope, drag to reorder` }}
            </p>

            <draggable
                v-model="local_envelope_documents"
                :animation="150"
                @start="onDocumentDragStart"
                @end="onDocumentDragEnd"
                class="space-y-2"
            >
                <div v-for="(document, index) in local_envelope_documents"
                    :key="document?.id || index"
                    :data-id="document?.id"
                    class="flex items-center py-2 px-3 bg-white rounded border border-gray-200 hover:bg-gray-50 hover:border-blue-400 hover:shadow-md transition-all duration-200 cursor-move"
                >
                    <file-icon size="14"
                        class="flex-none mr-2 mb-0.5 text-blue-600"
                        :stroke-width="2"
                        fill="none"
                    />

                    <div class="flex-1 min-w-0 font-semibold text-sm leading-snug text-gray-800 truncate select-none">
                        {{ document?.display_filename || 'Unknown Document' }}
                    </div>

                    <button
                        @click="removeDocument(document)"
                        class="flex-none flex flex-col items-start ml-2 -mr-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Remove document"
                    >
                        <i class="fa fa-minus-circle text-sm"></i>
                    </button>
                </div>
            </draggable>
        </div>

        <div v-else>
            <div v-for="(document, index) in local_envelope_documents"
                class="flex items-center py-2"
            >
                <file-icon size="14"
                    class="flex-none mr-2 mb-0.5 text-blue-600"
                    :stroke-width="2"
                    fill="none"
                />

                <div class="flex-1 min-w-0 font-semibold text-sm leading-snug text-gray-800 truncate select-none">
                    {{ document?.display_filename || 'Unknown Document' }}
                </div>
            </div>
        </div>

        <div v-if="editable"
            class="w-full flex justify-end"
        >
            <button-blue-outline
                size="small"
                @click="showDocumentSelectModal"
            >
                {{ $t('Select documents') }}
            </button-blue-outline>
        </div>

        <div class="absolute top-0 right-0">
            <toast-message :message="toast" />
        </div>

        <envelope-document-select-modal
            :show="show_document_select_modal"
            :property_id="property_id"
            :property="property"
            :envelope_documents="envelope_documents"
            :documents="documents"
            :templates="templates"
            :document_folders="document_folders"
            @selected="handleDocumentSelection"
            @closed="closeDocumentSelectModal"
        />
    </div>
</template>

<script>
import ButtonBlueOutline from '../../../components/button-blue-outline.vue';
import EnvelopeDocumentSelectModal from './envelope-document-select-modal.vue';
import ToastMessage from '../../../components/toast-message.vue';
import { FileIcon } from 'vue-feather-icons';
import draggable from 'vuedraggable';
import axios from 'axios';

export default {
    components: {
        ButtonBlueOutline,
        EnvelopeDocumentSelectModal,
        ToastMessage,
        FileIcon,
        draggable,
    },

    props: {
        property_id: { required: true },
        property: {},
        documents: {},
        templates: {},
        document_folders: {},
        envelope: { default: null },
        envelope_documents: { default: null },
        toast: {},
    },

    data() {
        return {
            loading: false,
            show_document_select_modal: false,
            local_envelope_documents: this.envelope_documents || [],
            is_dragging: false,
        }
    },

    computed: {
        editable() {
            return ! this.envelope?.sent_at;
        },
    },

    methods: {
        showDocumentSelectModal() {
            this.show_document_select_modal = true;
        },
        closeDocumentSelectModal() {
            this.show_document_select_modal = false;
        },
        handleDocumentSelection(selected_documents) {
            this.$emit('documents-updated', selected_documents);
        },
        removeDocument(document) {
            const index = this.local_envelope_documents.findIndex(d => d.id === document.id);

            if (index > -1) {
                this.local_envelope_documents.splice(index, 1);
            }

            this.$emit('documents-updated', this.local_envelope_documents);
        },
        onDocumentDragStart(event) {
            this.is_dragging = true;

            // Set cursor to move on the document body
            document.body.style.cursor = 'move';

            // Create a proper drag image that will follow the cursor
            if (event.dataTransfer && event.dataTransfer.setDragImage) {
                // Find the document div element
                let dragElement = event.target;
                while (dragElement && !dragElement.classList.contains('flex')) {
                    dragElement = dragElement.parentElement;
                }
                
                if (dragElement) {
                    // Create a clone of the element for the drag image
                    const dragImage = dragElement.cloneNode(true);
                    dragImage.style.cssText = `
                        position: absolute;
                        top: -1000px;
                        left: -1000px;
                        width: ${dragElement.offsetWidth}px;
                        height: ${dragElement.offsetHeight}px;
                        background: white;
                        border: 1px solid #3b82f6;
                        border-radius: 4px;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                        opacity: 0.9;
                        z-index: 9999;
                        pointer-events: none;
                    `;
                    
                    // Copy the content
                    dragImage.innerHTML = dragElement.innerHTML;
                    
                    document.body.appendChild(dragImage);
                    event.dataTransfer.setDragImage(dragImage, 10, 10);

                    // Clean up the drag image after a short delay
                    setTimeout(() => {
                        if (document.body.contains(dragImage)) {
                            document.body.removeChild(dragImage);
                        }
                    }, 100);
                }
            }
        },
        onDocumentDragEnd() {
            this.is_dragging = false;

            // Reset cursor to default
            document.body.style.cursor = 'default';

            // Emit the updated documents to parent
            this.$emit('documents-updated', this.local_envelope_documents);
        },
        getFolderName(folder_id) {
            if (!folder_id || !Array.isArray(this.document_folders)) {
                return 'Unknown Folder';
            }
            
            const folder = this.document_folders.find(f => f.id == folder_id);
            return folder ? folder.name : 'Unknown Folder';
        },
    },

    mounted() {
        this.local_envelope_documents = Array.isArray(this.envelope_documents)
            ? [...this.envelope_documents]
            : []
    },

    watch: {
        envelope_documents: {
            handler(newDocuments) {
                this.local_envelope_documents = Array.isArray(newDocuments) ? [...newDocuments] : [];
            },
            deep: true
        }
    },
}
</script>
